import type { MonetaryAmount } from '@backmarket/http-api'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'

export const QUOTES_LISTING_GRADES = {
  NOT_FUNCTIONAL_CRACKED: 'NOT_FUNCTIONAL_CRACKED',
  NOT_FUNCTIONAL_USED: 'NOT_FUNCTIONAL_USED',
  FUNCTIONAL_CRACKED: 'FUNCTIONAL_CRACKED',
  FUNCTIONAL_USED: 'FUNCTIONAL_USED',
  FUNCTIONAL_GOOD: 'FUNCTIONAL_GOOD',
  FUNCTIONAL_FLAWLESS: 'FUNCTIONAL_FLAWLESS',
} as const

export const CATEGORIES = {
  AUDIO: 'AUDIO',
  GAMING_CONSOLE: 'GAMING_CONSOLE',
  MACBOOK: 'MACBOOK',
  SMARTPHONE: 'SMARTPHONE',
  TABLET: 'TABLET',
} as const

export const ORDER_BY = {
  PROFIT_POTENTIAL: 'PROFIT_POTENTIAL',
  PRICE_TO_WIN: 'PRICE_TO_WIN',
  MARGIN: 'MARGIN',
  SELLING_PRICE: 'SELLING_PRICE',
  ORDER_VOLUME: 'ORDER_VOLUME',
  COMPETITORS_COUNT: 'COMPETITORS_COUNT',
} as const

export const COMPETITION_LEVELS = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
  VERY_LOW: 'VERY_LOW',
} as const

export const ACTIONS = {
  CREATE_LISTING: 'CREATE_LISTING',
  TURN_ONLINE: 'TURN_ONLINE',
  UPDATE_PRICE: 'UPDATE_PRICE',
} as const

export const SORT_ORDER = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const

export type QUOTES_GRADES_ENUM = keyof typeof QUOTES_LISTING_GRADES
export type CATEGORIES_ENUM = keyof typeof CATEGORIES
export type ORDER_BY_ENUM = keyof typeof ORDER_BY
export type COMPETITION_LEVELS_ENUM = keyof typeof COMPETITION_LEVELS
export type ACTIONS_ENUM = keyof typeof ACTIONS
export type SORT_ORDER_ENUM = keyof typeof SORT_ORDER

export const LISTING_PUBLICATION_STATE = {
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
} as const

export type ListingStateType = keyof typeof LISTING_PUBLICATION_STATE

export const BuyBackQuoteListingStates = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
} as const

export type BuyBackQuoteListingState = keyof typeof BuyBackQuoteListingStates

export interface BuyBackQuote {
  marketCode: MarketCountryCode
  product: BuyBackQuoteProduct
  listings: Array<BuyBackQuoteListing>
}

export interface BuyBackQuotes {
  totalCount: number
  quotes: Array<BuyBackQuote>
}

export interface BuybackQuotePostDetails {
  listingId: string
  productId: string
  grade: QUOTES_GRADES_ENUM
  status: number
  title: string
  type: string
}

export interface BuyBackQuoteProduct {
  productId: string
  title: string
  image: string
  availableMarkets?: MarketCountryCode[]
}

export interface BuyBackQuoteListing {
  listingId: string | null
  backboxPrice: MonetaryAmount | null
  grade: QUOTES_GRADES_ENUM
  hasBackbox: boolean
  price: MonetaryAmount
  priceToWinBackbox: MonetaryAmount | null
  state: ListingStateType
  sku: string | null
}

export interface PostBuyBackQuoteListing {
  grade: string
  price?: MonetaryAmount | null
  sku?: string | null
  state?: BuyBackQuoteListingState
}

export interface BuyBackQuotePricingEstimation {
  grade: QUOTES_GRADES_ENUM
  priceToWinBackbox: MonetaryAmount
}

export type QuotesQuery = {
  pageNumber?: number
  pageSize?: number
  marketCode?: MarketCountryCode
  productId?: string
  authorization?: string
  releaseYear?: number
  sku?: string
  hasBackbox?: boolean
  grades?: QUOTES_GRADES_ENUM[]
  brand?: string
  model?: string
}

export type GetListingsImportMetadata = {
  csvColumns: Array<string>
  csvTemplateLink: string
}

export type PostListings = {
  task: number
}

export type OpportunitiesQuery = {
  marketCodes?: MarketCountryCode[]
  model?: string
  brand?: string
  grades?: QUOTES_GRADES_ENUM[]
  categories?: CATEGORIES_ENUM[]
  pageNumber?: number
  pageSize?: string
  sortBy?: ORDER_BY_ENUM
  sortOrder?: SORT_ORDER_ENUM
  competitionLevels?: COMPETITION_LEVELS_ENUM[]
}

export type SourcingOpportunity = {
  productId: string
  profitPotentialRank: number
  productTitle: string
  grade: QUOTES_GRADES_ENUM
  marketCode: MarketCountryCode
  priceToWinBackboxEstimation: MonetaryAmount
  margin: MonetaryAmount
  action: ACTIONS_ENUM
  competitionLevel: COMPETITION_LEVELS_ENUM
  ordersVolume: string
  averageSellingPrice: MonetaryAmount
}

export type Opportunities = {
  pageNumber: number
  pageSize: number
  pageCount: number
  totalCount: number
  lastUpdatedAt: string | null
  sourcingOpportunities: Array<SourcingOpportunity>
}
